import { apiRequest } from './client';

/**
 * PayPal/Zettle Entrega 1: API de intentos de cobro CLIENT-MEDIATED (nombres provider-neutral).
 *
 * El backend es la verdad: entrega el intento (con SU importe en centavos) y decide qué transición
 * admite cada observación. El negocio jamás viaja en el request — se deriva de la sesión.
 */

export type AttemptStatus = 'CREATED' | 'SUBMITTED' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'CANCELED' | 'UNKNOWN';

export interface StartPaymentAttemptResponse {
  attemptId: string;
  status: AttemptStatus;
  /** true = intento YA existente (doble toque o recuperación): la MISMA referencia, jamás una nueva. */
  reused: boolean;
  paymentId: string;
  amount: string;
  /** Importe en centavos calculado por el SERVIDOR: es lo que viaja al SDK, tal cual. */
  amountMinor: number;
  currency: string;
  providerKey: string;
}

export type ObservationSource = 'PAYMENT_RESULT' | 'RETRIEVE' | 'RECOVERY';
export type ObservedOutcome = 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'UNKNOWN';

export interface PaymentObservationInput {
  source: ObservationSource;
  outcome: ObservedOutcome;
  providerTransactionId?: string;
  rawReason?: string;
  observedAmountMinor?: number;
  observedCurrency?: string;
}

export interface PaymentObservationResponse {
  applied: boolean;
  attemptStatus: AttemptStatus;
  paymentStatus: string;
  saleId: string;
  saleCompleted: boolean;
  message: string;
}

/** Entrega (o devuelve, idempotente) el intento de cobro con lector para una venta pendiente. */
export const startPaymentAttempt = (
  saleId: string,
  /**
   * PAYPAL-CURRENCY-01: lo que el equipo dice de su propia sesión del lector. NO es una entrada
   * financiera: el importe, la moneda y la cuenta los sigue poniendo el servidor, y esto solo puede
   * provocar un rechazo. Si va vacío, el servidor no bloquea.
   */
  deviceSession?: { currency?: string; organizationSuffix?: string },
) =>
  apiRequest<StartPaymentAttemptResponse>(`/api/sales/${saleId}/payment-attempts`, {
    method: 'POST',
    body: JSON.stringify(deviceSession ? { deviceSession } : {}),
  });

/** Reporta lo que el SDK devolvió al dispositivo. Reintentable sin límite: es idempotente. */
export const reportPaymentObservation = (attemptId: string, input: PaymentObservationInput) =>
  apiRequest<PaymentObservationResponse>(`/api/payment-attempts/${attemptId}/observations`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
